import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { companyAPI, mediaAPI, usersAPI } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  Loader2, 
  Building2, 
  Users, 
  ShoppingBag, 
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Package,
  ShoppingCart,
  UserCheck,
  Zap,
  AlertCircle,
  Calculator,
  Search,
  CreditCard,
  Upload,
  ImageIcon,
  Images,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { MigrateLegacyPayHereDialog } from '../components/MigrateLegacyPayHereDialog';
import StorefrontMediaManager from '../components/StorefrontMediaManager';
import StorefrontBrandingManager from '../components/StorefrontBrandingManager';
import CloudinaryMediaLibraryDialog from '../components/CloudinaryMediaLibraryDialog';

const DEFAULT_STOREFRONT_MESSAGE = 'Something new is taking shape. We will be live soon.';
const EMPTY_STOREFRONT_BRANDING = {
  site_logo: null,
  header_logo: null,
  footer_logo: null,
  storefront_browser_logo: null,
  erp_browser_logo: null,
};
const normaliseStorefrontBranding = (branding = {}) => ({
  site_logo: branding.site_logo || null,
  header_logo: branding.header_logo || null,
  footer_logo: branding.footer_logo || null,
  storefront_browser_logo: branding.storefront_browser_logo || null,
  erp_browser_logo: branding.erp_browser_logo || null,
});

const formatColomboDateTimeInput = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '2026-07-25T00:00';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Colombo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value;
  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}`;
};

const colomboDateTimeToIso = (value) => new Date(`${value}:00+05:30`).toISOString();

const CollapsibleSettingsSection = ({
  title,
  description,
  open,
  onToggle,
  children,
  testId,
}) => (
  <section className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={testId}>
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left transition-colors hover:bg-slate-50 sm:px-5"
      aria-expanded={open}
    >
      <div>
        <h3 className="font-semibold text-slate-900">{title}</h3>
        <p className="mt-1 text-sm text-slate-500">{description}</p>
      </div>
      <ChevronDown className={`h-5 w-5 shrink-0 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && <div className="border-t border-slate-200 p-4 sm:p-5">{children}</div>}
  </section>
);

export const Settings = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') || 'company';
  const allowedTabs = user?.role === 'admin'
    ? ['company', 'storefront', 'woocommerce', 'users', 'system']
    : ['company', 'woocommerce'];
  const activeTab = allowedTabs.includes(requestedTab) ? requestedTab : 'company';
  const [storefrontPanels, setStorefrontPanels] = useState({
    branding: false,
    media: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [removingLogo, setRemovingLogo] = useState(false);
  const [logoLibraryOpen, setLogoLibraryOpen] = useState(false);
  const [company, setCompany] = useState(null);
  const [wooSettings, setWooSettings] = useState({
    store_url: '',
    consumer_key: '',
    consumer_secret: '',
    enabled: false,
    auto_sync_enabled: false,
    auto_sync_interval: 60,
  });
  const [storefrontSettings, setStorefrontSettings] = useState({
    under_construction: false,
    launch_at: '2026-07-25T00:00',
    message: DEFAULT_STOREFRONT_MESSAGE,
    media: {},
    branding: { ...EMPTY_STOREFRONT_BRANDING },
  });
  const [savedStorefrontBranding, setSavedStorefrontBranding] = useState({ ...EMPTY_STOREFRONT_BRANDING });
  const [savingStorefront, setSavingStorefront] = useState(false);
  const [users, setUsers] = useState([]);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    full_name: '',
    email: '',
    password: '',
  });
  
  // WooCommerce sync states
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // System/Admin states
  const [recomputing, setRecomputing] = useState(false);
  const [fixingPayments, setFixingPayments] = useState(false);
  const [backfillingTasks, setBackfillingTasks] = useState(false);
  const [backfillingWooSales, setBackfillingWooSales] = useState(false);
  const [migratePayHereOpen, setMigratePayHereOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [trailCode, setTrailCode] = useState('2110');
  const [trailLoading, setTrailLoading] = useState(false);
  const [trailResult, setTrailResult] = useState(null);

  const handleRecomputeBalances = async () => {
    setRecomputing(true);
    try {
      const preview = await api.get('/finance/admin/recompute-balances/preview');
      const count = preview.data?.change_count || 0;
      if (!count) {
        toast.success('All cached balances already match the journal ledger');
        return;
      }
      const examples = (preview.data?.changes || []).slice(0, 8).map((item) => `${item.code}: ${item.current_balance} → ${item.expected_balance}`).join('\n');
      if (!window.confirm(`${count} account balance(s) will change as of ${preview.data.as_of_date}:\n\n${examples}\n\nApply these changes?`)) return;
      const res = await api.post('/finance/admin/recompute-balances', {
        confirmation_code: 'RECOMPUTE BALANCES', expected_change_count: count,
        as_of_date: preview.data.as_of_date,
      });
      toast.success(res.data?.message || 'Balances recomputed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to recompute balances');
    } finally {
      setRecomputing(false);
    }
  };

  const handleFixSupplierPayments = async () => {
    if (!window.confirm('This will re-route any supplier payments that were wrongly booked to Advance-to-Suppliers (1245), so they correctly clear Trade Payables (2110). Then recomputes balances. Continue?')) return;
    setFixingPayments(true);
    try {
      const res = await api.post('/finance/admin/fix-supplier-payments');
      toast.success(res.data?.message || 'Supplier payments fixed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fix supplier payments');
    } finally {
      setFixingPayments(false);
    }
  };

  const handleBackfillTaskAccruals = async () => {
    if (!window.confirm('This will create accrual journal entries for any verified task that has a payment amount but no liability entry, so amounts owed to employees show up under Salaries Payable (2310). Continue?')) return;
    setBackfillingTasks(true);
    try {
      const res = await api.post('/finance/admin/backfill-task-accruals');
      toast.success(res.data?.message || 'Task accruals created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to backfill task accruals');
    } finally {
      setBackfillingTasks(false);
    }
  };

  const handleBackfillWooSales = async () => {
    if (!window.confirm('This will create missing journal entries for existing WooCommerce sales orders so they appear under Quick Transactions, General Ledger, and Trial Balance. Continue?')) return;
    setBackfillingWooSales(true);
    try {
      const res = await api.post('/finance/admin/backfill-woo-sales-ledger');
      toast.success(res.data?.message || 'WooCommerce sales backfilled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to backfill WooCommerce sales');
    } finally {
      setBackfillingWooSales(false);
    }
  };

  const handleScanImbalanced = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const res = await api.get('/finance/admin/find-imbalanced-entries');
      setScanResult(res.data);
      const total = (res.data.imbalanced_count || 0) + (res.data.orphan_line_count || 0);
      if (total === 0) toast.success('No imbalances found — books are clean');
      else toast.warning(`Found ${total} issue(s) — review below`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to scan');
    } finally {
      setScanning(false);
    }
  };

  const handleDeleteEntry = async (entryId, entryNumber) => {
    if (!window.confirm(`Permanently delete journal entry ${entryNumber}? This cannot be undone.`)) return;
    try {
      await api.delete(`/finance/admin/journal-entry/${entryId}`);
      toast.success(`Deleted ${entryNumber}`);
      handleScanImbalanced();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleAccountTrail = async () => {
    if (!trailCode.trim()) return;
    setTrailLoading(true);
    setTrailResult(null);
    try {
      const res = await api.get(`/finance/admin/account-trail/${trailCode.trim()}`);
      setTrailResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load account trail');
    } finally {
      setTrailLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [companyResult, wooResult, storefrontResult, usersResult] = await Promise.allSettled([
        companyAPI.get(),
        companyAPI.getWooSettings(),
        companyAPI.getStorefrontSettings(),
        user?.role === 'admin' ? usersAPI.getAll() : Promise.resolve({ data: [] }),
      ]);

      // Company details are required for this page. Optional integrations must not
      // prevent the rest of Settings from loading when one endpoint is unavailable.
      if (companyResult.status === 'rejected') {
        throw companyResult.reason;
      }
      setCompany(companyResult.value.data);

      const optionalFailures = [];

      if (wooResult.status === 'fulfilled' && wooResult.value.data) {
        const wooData = wooResult.value.data;
        setWooSettings({
          store_url: wooData.store_url || '',
          consumer_key: wooData.consumer_key || '',
          consumer_secret: wooData.consumer_secret || '',
          enabled: wooData.enabled || false,
          auto_sync_enabled: wooData.auto_sync_enabled || false,
          auto_sync_interval: wooData.auto_sync_interval || 60,
        });
        if (wooData.enabled) {
          fetchSyncLogs();
          fetchSyncStatus();
        }
      } else if (wooResult.status === 'rejected') {
        optionalFailures.push('WooCommerce');
        console.error('Failed to load WooCommerce settings', wooResult.reason);
      }

      if (storefrontResult.status === 'fulfilled' && storefrontResult.value.data) {
        const storefrontData = storefrontResult.value.data;
        const branding = normaliseStorefrontBranding(storefrontData.branding);
        setStorefrontSettings({
          under_construction: Boolean(storefrontData.under_construction),
          launch_at: formatColomboDateTimeInput(storefrontData.launch_at),
          message: storefrontData.message || DEFAULT_STOREFRONT_MESSAGE,
          media: storefrontData.media || {},
          branding,
        });
        setSavedStorefrontBranding(branding);
      } else if (storefrontResult.status === 'rejected') {
        optionalFailures.push('storefront');
        console.error('Failed to load storefront settings', storefrontResult.reason);
      }

      if (usersResult.status === 'fulfilled') {
        setUsers(usersResult.value.data);
      } else {
        optionalFailures.push('users');
        console.error('Failed to load users', usersResult.reason);
      }

      if (optionalFailures.length > 0) {
        toast.warning(`Some optional settings could not be loaded: ${optionalFailures.join(', ')}`);
      }
    } catch (error) {
      console.error('Failed to load company settings', error);
      toast.error(error.response?.data?.detail || 'Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncLogs = async () => {
    try {
      const response = await api.get('/woocommerce/sync-logs', { params: { limit: 10 } });
      setSyncLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch sync logs');
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await api.get('/woocommerce/sync-status');
      setSyncStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch sync status');
    }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  const handleSaveCompany = async () => {
    setSaving(true);
    try {
      const response = await companyAPI.update(company);
      setCompany(response.data);
      window.dispatchEvent(new CustomEvent('company-branding-updated', {
        detail: { name: response.data.name, logo: response.data.logo || null },
      }));
      toast.success('Company settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const saveCompanyLogoAsset = async (asset, successMessage = 'Company logo updated') => {
    const previousLogo = company?.logo;
    const logo = { ...asset, alt: asset?.alt || company?.name || 'Company logo' };
    const response = await companyAPI.updateLogo(logo);
    const savedLogo = response.data.logo;
    const nextCompany = { ...company, logo: savedLogo };
    setCompany(nextCompany);
    window.dispatchEvent(new CustomEvent('company-branding-updated', {
      detail: { name: nextCompany.name, logo: savedLogo },
    }));
    toast.success(successMessage);

    if (previousLogo?.public_id && previousLogo.public_id !== savedLogo?.public_id) {
      mediaAPI.deleteAsset(previousLogo.public_id).catch((error) => {
        console.warn('The previous logo is still used elsewhere or could not be removed', error);
      });
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadingLogo(true);
    try {
      const uploaded = await mediaAPI.uploadFile(file);
      await saveCompanyLogoAsset(uploaded, 'Company logo uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to upload company logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!company?.logo || !window.confirm('Remove the Company Logo? Dedicated storefront and browser logos will remain unchanged.')) return;
    const previousLogo = company.logo;
    setRemovingLogo(true);
    try {
      await companyAPI.removeLogo();
      const nextCompany = { ...company, logo: null };
      setCompany(nextCompany);
      window.dispatchEvent(new CustomEvent('company-branding-updated', {
        detail: { name: nextCompany.name, logo: null },
      }));
      toast.success('Company logo removed');
      if (previousLogo.public_id) {
        mediaAPI.deleteAsset(previousLogo.public_id).catch((error) => {
          console.warn('The removed logo is still used elsewhere or could not be deleted', error);
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove company logo');
    } finally {
      setRemovingLogo(false);
    }
  };

  const handleSaveWoo = async () => {
    setSaving(true);
    try {
      await companyAPI.updateWooSettings(wooSettings);
      toast.success('WooCommerce settings saved');
      if (wooSettings.enabled) {
        fetchSyncLogs();
        fetchSyncStatus();
      }
    } catch (error) {
      toast.error('Failed to save WooCommerce settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStorefront = async () => {
    if (!storefrontSettings.launch_at) {
      toast.error('Please choose the storefront launch date and time');
      return;
    }
    setSavingStorefront(true);
    try {
      const response = await companyAPI.updateStorefrontSettings({
        ...storefrontSettings,
        launch_at: colomboDateTimeToIso(storefrontSettings.launch_at),
        message: storefrontSettings.message.trim() || DEFAULT_STOREFRONT_MESSAGE,
      });
      const savedBranding = normaliseStorefrontBranding(response.data.branding);
      setStorefrontSettings((current) => ({
        ...current,
        branding: savedBranding,
      }));
      Object.keys(EMPTY_STOREFRONT_BRANDING).forEach((key) => {
        const previous = savedStorefrontBranding[key];
        const current = savedBranding[key];
        if (previous?.public_id && previous.public_id !== current?.public_id) {
          mediaAPI.deleteAsset(previous.public_id).catch((error) => {
            console.warn(`Previous ${key} could not be deleted`, error);
          });
        }
      });
      setSavedStorefrontBranding(savedBranding);
      window.dispatchEvent(new Event('storefront-branding-updated'));
      toast.success(storefrontSettings.under_construction
        ? 'Construction page and countdown are active'
        : 'Storefront is open to customers');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save storefront settings');
    } finally {
      setSavingStorefront(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const response = await api.get('/woocommerce/test-connection');
      setConnectionStatus(response.data);
      if (response.data.success) {
        toast.success('Connection successful!');
      } else {
        toast.error(response.data.message || 'Connection failed');
      }
    } catch (error) {
      setConnectionStatus({ success: false, message: error.response?.data?.detail || 'Connection failed' });
      toast.error('Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleManualSync = async (syncType) => {
    setSyncing(true);
    try {
      let endpoint = '/woocommerce/full-sync';
      let message = 'Full sync started';
      
      if (syncType === 'products') {
        endpoint = '/woocommerce/products/sync';
        message = 'Product sync started';
      } else if (syncType === 'orders') {
        endpoint = '/woocommerce/orders/sync';
        message = 'Order sync started';
      } else if (syncType === 'customers') {
        endpoint = '/woocommerce/customers/sync';
        message = 'Customer sync started';
      } else if (syncType === 'weights') {
        endpoint = '/woocommerce/sync/weights';
        message = 'Weight sync to WooCommerce started';
      }
      
      const response = await api.post(endpoint);
      toast.success(message);
      
      // Poll for completion
      setTimeout(() => {
        fetchSyncLogs();
        fetchSyncStatus();
        setSyncing(false);
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
      setSyncing(false);
    }
  };

  const handleAddUser = async () => {
    setSaving(true);
    try {
      await usersAPI.create(newUser);
      toast.success('User created successfully');
      setUserDialogOpen(false);
      setNewUser({ full_name: '', email: '', password: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async (userId, role) => {
    try {
      await usersAPI.updateRole(userId, role);
      toast.success('Role updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await usersAPI.delete(userId);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Settings
        </h2>
        <p className="text-slate-500 mt-1">Manage your company and system settings</p>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setSearchParams({ tab: value }, { replace: true })} className="space-y-4">
        <TabsList>
          <TabsTrigger value="company">Company</TabsTrigger>
          {user?.role === 'admin' && <TabsTrigger value="storefront">Storefront</TabsTrigger>}
          <TabsTrigger value="woocommerce">WooCommerce</TabsTrigger>
          {user?.role === 'admin' && <TabsTrigger value="users">Users</TabsTrigger>}
          {user?.role === 'admin' && <TabsTrigger value="system" data-testid="tab-system">System</TabsTrigger>}
        </TabsList>

        {/* Company Settings */}
        <TabsContent value="company">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-indigo-100">
                  <Building2 className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <CardTitle>Company Information</CardTitle>
                  <CardDescription>Basic company details and preferences</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  <div className="flex h-24 w-40 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white p-3">
                    {company?.logo?.url ? (
                      <img
                        src={company.logo.url}
                        alt={company.logo.alt || `${company?.name || 'Company'} logo`}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <ImageIcon className="h-9 w-9 text-slate-300" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <Label className="text-base">Company Logo</Label>
                    <p className="mt-1 text-sm text-slate-500">
                      Your primary company identity. Dedicated storefront and browser logos are managed separately in the Storefront tab.
                      This logo is used only as a fallback when a dedicated asset has not been uploaded.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button type="button" variant="outline" onClick={() => setLogoLibraryOpen(true)} disabled={uploadingLogo || removingLogo}>
                        <Images className="mr-2 h-4 w-4" /> Choose from Cloudinary
                      </Button>
                      <Button asChild variant="outline" disabled={uploadingLogo || removingLogo}>
                        <label className="cursor-pointer">
                          {uploadingLogo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                          {company?.logo ? 'Replace Logo' : 'Upload Logo'}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/avif"
                            className="hidden"
                            onChange={handleLogoUpload}
                            disabled={uploadingLogo || removingLogo}
                            data-testid="company-logo-input"
                          />
                        </label>
                      </Button>
                      {company?.logo && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleLogoRemove}
                          disabled={uploadingLogo || removingLogo}
                          className="text-red-600 hover:text-red-700"
                        >
                          {removingLogo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={company?.name || ''}
                    onChange={(e) => setCompany({ ...company, name: e.target.value })}
                    data-testid="company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={company?.email || ''}
                    onChange={(e) => setCompany({ ...company, email: e.target.value })}
                    data-testid="company-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={company?.phone || ''}
                    onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                    data-testid="company-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select
                    value={company?.currency || 'LKR'}
                    onValueChange={(v) => setCompany({ ...company, currency: v })}
                  >
                    <SelectTrigger data-testid="company-currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LKR">LKR - Sri Lankan Rupee</SelectItem>
                      <SelectItem value="USD">USD - US Dollar</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                      <SelectItem value="GBP">GBP - British Pound</SelectItem>
                      <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select
                    value={company?.timezone || 'Asia/Colombo'}
                    onValueChange={(v) => setCompany({ ...company, timezone: v })}
                  >
                    <SelectTrigger data-testid="company-timezone">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Asia/Colombo">Asia/Colombo (Sri Lanka)</SelectItem>
                      <SelectItem value="UTC">UTC</SelectItem>
                      <SelectItem value="America/New_York">America/New York</SelectItem>
                      <SelectItem value="Europe/London">Europe/London</SelectItem>
                      <SelectItem value="Asia/Dubai">Asia/Dubai</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tax Rate (%)</Label>
                  <Input
                    type="number"
                    value={company?.tax_rate || 0}
                    onChange={(e) => setCompany({ ...company, tax_rate: parseFloat(e.target.value) || 0 })}
                    data-testid="company-tax"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={company?.address || ''}
                  onChange={(e) => setCompany({ ...company, address: e.target.value })}
                  data-testid="company-address"
                />
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={handleSaveCompany}
                  disabled={saving}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="save-company-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Storefront launch controls */}
        {user?.role === 'admin' && (
          <TabsContent value="storefront">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-lime-100">
                    <ShoppingBag className="w-5 h-5 text-lime-700" />
                  </div>
                  <div>
                    <CardTitle>Storefront Launch</CardTitle>
                    <CardDescription>
                      Show a branded construction page and live countdown before the store opens
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between gap-6 rounded-lg bg-slate-50 p-4">
                  <div>
                    <p className="font-medium">Under construction mode</p>
                    <p className="text-sm text-slate-500">
                      Customers will see the construction page. ERP admins can use “Preview Storefront” from the top bar.
                    </p>
                  </div>
                  <Switch
                    checked={storefrontSettings.under_construction}
                    onCheckedChange={(checked) => setStorefrontSettings({
                      ...storefrontSettings,
                      under_construction: checked,
                    })}
                    data-testid="storefront-construction-mode"
                  />
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="storefront-launch-at">Launch date and time</Label>
                    <Input
                      id="storefront-launch-at"
                      type="datetime-local"
                      value={storefrontSettings.launch_at}
                      onChange={(event) => setStorefrontSettings({
                        ...storefrontSettings,
                        launch_at: event.target.value,
                      })}
                      data-testid="storefront-launch-at"
                    />
                    <p className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Clock className="h-3.5 w-3.5" /> Sri Lanka time (Asia/Colombo)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="storefront-launch-message">Customer message</Label>
                    <Input
                      id="storefront-launch-message"
                      value={storefrontSettings.message}
                      maxLength={200}
                      onChange={(event) => setStorefrontSettings({
                        ...storefrontSettings,
                        message: event.target.value,
                      })}
                      placeholder={DEFAULT_STOREFRONT_MESSAGE}
                      data-testid="storefront-launch-message"
                    />
                    <p className="text-xs text-slate-500">Maximum 200 characters</p>
                  </div>
                </div>

                <div className={`rounded-lg border p-4 ${
                  storefrontSettings.under_construction
                    ? 'border-amber-200 bg-amber-50 text-amber-800'
                    : 'border-emerald-200 bg-emerald-50 text-emerald-800'
                }`}>
                  <p className="font-medium">
                    {storefrontSettings.under_construction
                      ? 'The construction page will remain active until an administrator switches this mode off.'
                      : 'The complete storefront is currently visible to customers.'}
                  </p>
                  <p className="mt-1 text-sm opacity-80">
                    The date controls the countdown display only. The storefront remains protected until this switch is turned off.
                  </p>
                </div>

                <CollapsibleSettingsSection
                  title="Storefront & ERP Brand Assets"
                  description="Header, footer, storefront browser, and ERP browser logos."
                  open={storefrontPanels.branding}
                  onToggle={() => setStorefrontPanels((current) => ({ ...current, branding: !current.branding }))}
                  testId="storefront-brand-assets-collapse"
                >
                  <StorefrontBrandingManager
                    branding={storefrontSettings.branding}
                    companyLogo={company?.logo || null}
                    onChange={(branding) => setStorefrontSettings({ ...storefrontSettings, branding })}
                    disabled={savingStorefront}
                    showHeader={false}
                  />
                </CollapsibleSettingsSection>

                <CollapsibleSettingsSection
                  title="Storefront Images"
                  description="Homepage, collection, fit, campaign, and information-page images."
                  open={storefrontPanels.media}
                  onToggle={() => setStorefrontPanels((current) => ({ ...current, media: !current.media }))}
                  testId="storefront-images-collapse"
                >
                  <StorefrontMediaManager
                    media={storefrontSettings.media}
                    onChange={(media) => setStorefrontSettings({ ...storefrontSettings, media })}
                    disabled={savingStorefront}
                    showHeader={false}
                  />
                </CollapsibleSettingsSection>

                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveStorefront}
                    disabled={savingStorefront}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="save-storefront-settings"
                  >
                    {savingStorefront ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Save Storefront Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* WooCommerce Settings */}
        <TabsContent value="woocommerce">
          <div className="space-y-6">
            {/* Connection Settings Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-violet-100">
                    <ShoppingBag className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <CardTitle>WooCommerce Connection</CardTitle>
                    <CardDescription>Connect your WooCommerce store for two-way sync</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">Enable Integration</p>
                    <p className="text-sm text-slate-500">Turn on/off WooCommerce sync</p>
                  </div>
                  <Switch
                    checked={wooSettings.enabled}
                    onCheckedChange={(checked) => setWooSettings({ ...wooSettings, enabled: checked })}
                    data-testid="woo-enabled"
                  />
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Store URL</Label>
                    <Input
                      value={wooSettings.store_url}
                      onChange={(e) => setWooSettings({ ...wooSettings, store_url: e.target.value })}
                      placeholder="https://yourstore.com"
                      data-testid="woo-url"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Consumer Key</Label>
                    <Input
                      value={wooSettings.consumer_key}
                      onChange={(e) => setWooSettings({ ...wooSettings, consumer_key: e.target.value })}
                      placeholder="ck_xxxxxxxxxxxxxxxx"
                      data-testid="woo-key"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Consumer Secret</Label>
                    <Input
                      type="password"
                      value={wooSettings.consumer_secret}
                      onChange={(e) => setWooSettings({ ...wooSettings, consumer_secret: e.target.value })}
                      placeholder="cs_xxxxxxxxxxxxxxxx"
                      data-testid="woo-secret"
                    />
                  </div>
                </div>

                {/* Connection Status */}
                {connectionStatus && (
                  <div className={`p-4 rounded-lg border ${connectionStatus.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center gap-2">
                      {connectionStatus.success ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <span className={connectionStatus.success ? 'text-green-700' : 'text-red-700'}>
                        {connectionStatus.message}
                      </span>
                    </div>
                    {connectionStatus.store_info && (
                      <div className="mt-2 text-sm text-green-600">
                        WooCommerce v{connectionStatus.store_info.wc_version} | WordPress v{connectionStatus.store_info.wp_version}
                      </div>
                    )}
                  </div>
                )}

                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> To get your API credentials, go to your WooCommerce store → 
                    Settings → Advanced → REST API → Add Key. Select Read/Write permissions.
                  </p>
                </div>

                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    onClick={handleTestConnection}
                    disabled={testingConnection || !wooSettings.store_url}
                    data-testid="test-connection-btn"
                  >
                    {testingConnection ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                    Test Connection
                  </Button>
                  <Button
                    onClick={handleSaveWoo}
                    disabled={saving}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="save-woo-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save Settings
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Auto Sync Settings Card */}
            {wooSettings.enabled && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-100">
                      <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle>Automatic Sync</CardTitle>
                      <CardDescription>Configure automatic synchronization schedule</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">Enable Auto Sync</p>
                      <p className="text-sm text-slate-500">Automatically sync every hour</p>
                    </div>
                    <Switch
                      checked={wooSettings.auto_sync_enabled}
                      onCheckedChange={(checked) => setWooSettings({ ...wooSettings, auto_sync_enabled: checked })}
                      data-testid="auto-sync-enabled"
                    />
                  </div>

                  {wooSettings.auto_sync_enabled && (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Auto sync is active</span>
                      </div>
                      <p className="text-sm text-green-600 mt-1">
                        Products, orders, and customers will sync every hour automatically.
                      </p>
                    </div>
                  )}

                  {syncStatus && (
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm font-medium text-slate-600 mb-2">Last Sync Status</p>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Last Run:</span>
                          <p className="font-medium">
                            {syncStatus.last_sync ? new Date(syncStatus.last_sync).toLocaleString() : 'Never'}
                          </p>
                        </div>
                        <div>
                          <span className="text-slate-500">Next Run:</span>
                          <p className="font-medium">
                            {syncStatus.next_sync ? new Date(syncStatus.next_sync).toLocaleString() : 'Not scheduled'}
                          </p>
                        </div>
                        <div>
                          <span className="text-slate-500">Status:</span>
                          <p className="font-medium capitalize">{syncStatus.status || 'Idle'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Manual Sync Card */}
            {wooSettings.enabled && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-100">
                      <RefreshCw className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <CardTitle>Manual Sync</CardTitle>
                      <CardDescription>Sync data manually when needed</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <Button
                      variant="outline"
                      className="h-24 flex-col gap-2"
                      onClick={() => handleManualSync('full')}
                      disabled={syncing}
                      data-testid="sync-all-btn"
                    >
                      {syncing ? <Loader2 className="w-6 h-6 animate-spin" /> : <RefreshCw className="w-6 h-6" />}
                      <span>Sync All</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-24 flex-col gap-2"
                      onClick={() => handleManualSync('products')}
                      disabled={syncing}
                      data-testid="sync-products-btn"
                    >
                      {syncing ? <Loader2 className="w-6 h-6 animate-spin" /> : <Package className="w-6 h-6" />}
                      <span>Sync Products</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-24 flex-col gap-2"
                      onClick={() => handleManualSync('orders')}
                      disabled={syncing}
                      data-testid="sync-orders-btn"
                    >
                      {syncing ? <Loader2 className="w-6 h-6 animate-spin" /> : <ShoppingCart className="w-6 h-6" />}
                      <span>Sync Orders</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-24 flex-col gap-2"
                      onClick={() => handleManualSync('customers')}
                      disabled={syncing}
                      data-testid="sync-customers-btn"
                    >
                      {syncing ? <Loader2 className="w-6 h-6 animate-spin" /> : <UserCheck className="w-6 h-6" />}
                      <span>Sync Customers</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-24 flex-col gap-2 border-emerald-300 hover:bg-emerald-50"
                      onClick={() => handleManualSync('weights')}
                      disabled={syncing}
                      data-testid="sync-weights-btn"
                      title="Push product & variation weights (KG) from ERP to WooCommerce. Overwrites Woo values."
                    >
                      {syncing ? <Loader2 className="w-6 h-6 animate-spin text-emerald-600" /> : <RefreshCw className="w-6 h-6 text-emerald-600" />}
                      <span className="text-emerald-700">Push Weights → Woo</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sync History Card */}
            {wooSettings.enabled && syncLogs.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-slate-100">
                        <Clock className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <CardTitle>Sync History</CardTitle>
                        <CardDescription>Recent synchronization logs</CardDescription>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={fetchSyncLogs}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead className="table-header-cell">Type</TableHead>
                        <TableHead className="table-header-cell">Status</TableHead>
                        <TableHead className="table-header-cell">Items</TableHead>
                        <TableHead className="table-header-cell">Started</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {syncLogs.map((log) => (
                        <TableRow key={log.id} className="table-row">
                          <TableCell className="table-cell capitalize">{log.sync_type}</TableCell>
                          <TableCell className="table-cell">
                            <Badge variant={log.status === 'completed' ? 'success' : log.status === 'failed' ? 'destructive' : 'secondary'}>
                              {log.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="table-cell">
                            {log.items_created || 0} created, {log.items_updated || 0} updated
                          </TableCell>
                          <TableCell className="table-cell text-slate-500">
                            {new Date(log.started_at).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* User Management */}
        {user?.role === 'admin' && (
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-100">
                      <Users className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <CardTitle>User Management</CardTitle>
                      <CardDescription>Manage team members and their roles</CardDescription>
                    </div>
                  </div>
                  <Button onClick={() => setUserDialogOpen(true)} className="gap-2 bg-indigo-600 hover:bg-indigo-700" data-testid="add-user-btn">
                    <Plus className="w-4 h-4" />
                    Add User
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead className="table-header-cell">Name</TableHead>
                      <TableHead className="table-header-cell">Email</TableHead>
                      <TableHead className="table-header-cell">Role</TableHead>
                      <TableHead className="table-header-cell">Joined</TableHead>
                      <TableHead className="table-header-cell w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} className="table-row">
                        <TableCell className="table-cell font-medium">{u.full_name}</TableCell>
                        <TableCell className="table-cell text-slate-500">{u.email}</TableCell>
                        <TableCell className="table-cell">
                          <Select
                            value={u.role}
                            onValueChange={(v) => handleUpdateRole(u.id, v)}
                            disabled={u.id === user.id}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="manager">Manager</SelectItem>
                              <SelectItem value="accounts">Accounts</SelectItem>
                              <SelectItem value="store">Store</SelectItem>
                              <SelectItem value="staff">Staff</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="table-cell text-slate-500">
                          {new Date(u.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="table-cell">
                          {u.id !== user.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteUser(u.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* System / Admin Tools — Recompute Balances + Account Trail */}
        {user?.role === 'admin' && (
          <TabsContent value="system" className="space-y-4">
            {/* Recompute Balances */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100">
                    <Calculator className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle>Recompute Account Balances</CardTitle>
                    <CardDescription>
                      Re-calculate every account balance from its journal-entry lines.
                      Use this when balances drift (e.g. Trade Payable not zero after payment).
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-4 text-sm text-amber-900 flex gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    Single source of truth: journal entries. After recompute, every account's
                    stored balance equals the sum of its postings, and headers are forced to zero.
                  </div>
                </div>
                <Button
                  onClick={handleRecomputeBalances}
                  disabled={recomputing}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="recompute-balances-btn"
                >
                  {recomputing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recomputing…</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" /> Recompute All Balances</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Fix Supplier Payments (specific repair for the 1245 → 2110 bug) */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-100">
                    <AlertCircle className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <CardTitle>Fix Supplier Payments → Trade Payables</CardTitle>
                    <CardDescription>
                      Re-route any past supplier payments that were wrongly booked to Advance-to-Suppliers (1245)
                      so they correctly clear Trade Payables (2110). Run this once if your AP balance shows the
                      original GRN amount even after paying.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handleFixSupplierPayments}
                  disabled={fixingPayments}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="fix-supplier-payments-btn"
                >
                  {fixingPayments ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fixing…</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" /> Fix Misrouted Supplier Payments</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Backfill Task Accruals (post verified tasks to Salaries Payable) */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100">
                    <UserCheck className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle>Backfill Task Accruals → Salaries Payable</CardTitle>
                    <CardDescription>
                      Books verified employee tasks with payment amounts as a liability (2310 Salaries Payable)
                      so amounts owed to staff appear under Liabilities until paid via payroll.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handleBackfillTaskAccruals}
                  disabled={backfillingTasks}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="backfill-task-accruals-btn"
                >
                  {backfillingTasks ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Posting…</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" /> Backfill Task Accruals</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Backfill WooCommerce sales ledger — posts missing SALE- JEs for synced Woo orders */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100">
                    <ShoppingCart className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle>Backfill WooCommerce Sales → Ledger</CardTitle>
                    <CardDescription>
                      Posts missing journal entries for existing WooCommerce sales orders
                      so they appear under Quick Transactions, General Ledger, and Trial Balance.
                      Safe to run repeatedly — skips orders that already have a SALE- entry.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handleBackfillWooSales}
                  disabled={backfillingWooSales}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="backfill-woo-sales-btn"
                >
                  {backfillingWooSales ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Posting…</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" /> Backfill WooCommerce Sales</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Migrate legacy PayHere sales from Main Bank to PayHere Clearing */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-indigo-100">
                    <CreditCard className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <CardTitle>Migrate Legacy PayHere Sales</CardTitle>
                    <CardDescription>
                      Older online sales were posted directly to the Main Bank Account. Select the ones paid via
                      PayHere to move them into the PayHere Clearing account and mark them as pending settlement.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => setMigratePayHereOpen(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="open-migrate-settings-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" /> Migrate Legacy PayHere Sales
                </Button>
              </CardContent>
            </Card>

            {/* Scan for imbalanced entries — finds the cause of trial balance drift */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-rose-100">
                    <AlertCircle className="w-5 h-5 text-rose-600" />
                  </div>
                  <div>
                    <CardTitle>Find Imbalanced / Orphan Journal Entries</CardTitle>
                    <CardDescription>
                      Scans every journal entry for (a) lopsided entries (debit ≠ credit),
                      and (b) lines posted to non-existent account codes. Use this when the
                      Trial Balance shows a difference.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={handleScanImbalanced}
                  disabled={scanning}
                  variant="outline"
                  data-testid="scan-imbalanced-btn"
                >
                  {scanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning…</> : <><Search className="w-4 h-4 mr-2" /> Scan Now</>}
                </Button>

                {scanResult && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className={`rounded-lg border p-3 ${scanResult.imbalanced_count > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                        <p className="text-xs uppercase tracking-wider text-slate-500">Lopsided Entries</p>
                        <p className="font-bold text-2xl mt-1">{scanResult.imbalanced_count}</p>
                      </div>
                      <div className={`rounded-lg border p-3 ${scanResult.orphan_line_count > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                        <p className="text-xs uppercase tracking-wider text-slate-500">Orphan Lines</p>
                        <p className="font-bold text-2xl mt-1">{scanResult.orphan_line_count}</p>
                      </div>
                      <div className="rounded-lg border bg-slate-50 p-3">
                        <p className="text-xs uppercase tracking-wider text-slate-500">Total Drift</p>
                        <p className="font-bold text-xl mt-1">{(scanResult.summary?.total_drift || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="rounded-lg border bg-slate-50 p-3">
                        <p className="text-xs uppercase tracking-wider text-slate-500">Orphan Net</p>
                        <p className="font-bold text-xl mt-1">{((scanResult.summary?.orphan_total_debit || 0) - (scanResult.summary?.orphan_total_credit || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      </div>
                    </div>

                    {scanResult.imbalanced_count > 0 && (
                      <div className="rounded-lg border border-red-200 overflow-hidden">
                        <div className="bg-red-50 px-4 py-2 text-sm font-semibold text-red-900">Lopsided Entries</div>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Entry #</TableHead>
                              <TableHead>Description</TableHead>
                              <TableHead className="text-right">Debit</TableHead>
                              <TableHead className="text-right">Credit</TableHead>
                              <TableHead className="text-right">Drift</TableHead>
                              <TableHead></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {scanResult.imbalanced_entries.map((e) => (
                              <TableRow key={e.id}>
                                <TableCell className="font-mono text-xs">{e.entry_number}</TableCell>
                                <TableCell className="text-sm">{e.description}</TableCell>
                                <TableCell className="text-right font-mono">{e.total_debit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                                <TableCell className="text-right font-mono">{e.total_credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                                <TableCell className="text-right font-mono text-red-600 font-bold">{e.drift.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                                <TableCell>
                                  <Button size="sm" variant="destructive" onClick={() => handleDeleteEntry(e.id, e.entry_number)} data-testid={`delete-je-${e.id}`}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}

                    {scanResult.orphan_line_count > 0 && (
                      <div className="rounded-lg border border-amber-200 overflow-hidden">
                        <div className="bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-900">Orphan Lines (account code missing from COA)</div>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Entry #</TableHead>
                              <TableHead>Description</TableHead>
                              <TableHead>Missing Code</TableHead>
                              <TableHead className="text-right">Debit</TableHead>
                              <TableHead className="text-right">Credit</TableHead>
                              <TableHead></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {scanResult.orphan_lines.map((o, i) => (
                              <TableRow key={`${o.entry_id}-${i}`}>
                                <TableCell className="font-mono text-xs">{o.entry_number}</TableCell>
                                <TableCell className="text-sm">{o.description}</TableCell>
                                <TableCell className="font-mono text-amber-700">{o.missing_code} ({o.account_name})</TableCell>
                                <TableCell className="text-right font-mono">{o.debit ? o.debit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : ''}</TableCell>
                                <TableCell className="text-right font-mono">{o.credit ? o.credit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : ''}</TableCell>
                                <TableCell>
                                  <Button size="sm" variant="destructive" onClick={() => handleDeleteEntry(o.entry_id, o.entry_number)}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Account Trail Diagnostic */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-sky-100">
                    <Search className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <CardTitle>Account Trail (Diagnose Drift)</CardTitle>
                    <CardDescription>
                      Inspect every journal-entry line that hit a specific account.
                      Useful for finding why a balance is off.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g. 2110 (Trade Payables) or 1310 (Finished Goods)"
                    value={trailCode}
                    onChange={(e) => setTrailCode(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAccountTrail()}
                    className="max-w-md"
                    data-testid="trail-code-input"
                  />
                  <Button
                    onClick={handleAccountTrail}
                    disabled={trailLoading}
                    variant="outline"
                    data-testid="trail-load-btn"
                  >
                    {trailLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load'}
                  </Button>
                </div>

                {trailResult && (
                  <div className="space-y-4 pt-2">
                    {/* Summary */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="rounded-lg border bg-slate-50 p-3">
                        <p className="text-xs uppercase tracking-wider text-slate-500">Account</p>
                        <p className="font-bold text-slate-900 text-base mt-1">
                          {trailResult.account.code} – {trailResult.account.name}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {trailResult.account.account_type} {trailResult.account.is_header && '(HEADER)'}
                        </p>
                      </div>
                      <div className="rounded-lg border bg-slate-50 p-3">
                        <p className="text-xs uppercase tracking-wider text-slate-500">Total Debit</p>
                        <p className="font-bold text-slate-900 text-lg mt-1">
                          {trailResult.totals.total_debit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                      <div className="rounded-lg border bg-slate-50 p-3">
                        <p className="text-xs uppercase tracking-wider text-slate-500">Total Credit</p>
                        <p className="font-bold text-slate-900 text-lg mt-1">
                          {trailResult.totals.total_credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                      <div className={`rounded-lg border p-3 ${Math.abs(trailResult.totals.drift) > 0.01 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                        <p className="text-xs uppercase tracking-wider text-slate-500">Drift</p>
                        <p className={`font-bold text-lg mt-1 ${Math.abs(trailResult.totals.drift) > 0.01 ? 'text-red-700' : 'text-emerald-700'}`}>
                          {trailResult.totals.drift.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          stored vs. expected
                        </p>
                      </div>
                    </div>

                    {/* Journal lines */}
                    <div className="rounded-lg border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Entry #</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="text-right">Debit</TableHead>
                            <TableHead className="text-right">Credit</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {trailResult.trail.length === 0 ? (
                            <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">No transactions found for this account</TableCell></TableRow>
                          ) : (
                            trailResult.trail.map((row, i) => (
                              <TableRow key={i} data-testid={`trail-row-${i}`}>
                                <TableCell className="font-mono text-xs">{row.date || '—'}</TableCell>
                                <TableCell className="font-mono text-xs">{row.entry_number}</TableCell>
                                <TableCell className="text-sm">
                                  {row.description}
                                  {row.line_description && (
                                    <span className="block text-xs text-slate-500">{row.line_description}</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right font-mono">
                                  {row.debit ? row.debit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : ''}
                                </TableCell>
                                <TableCell className="text-right font-mono">
                                  {row.credit ? row.credit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : ''}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Add User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Add New User</DialogTitle>
            <DialogDescription>Create a new team member account</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                value={newUser.full_name}
                onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                data-testid="new-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                data-testid="new-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                data-testid="new-user-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddUser} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-user-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MigrateLegacyPayHereDialog open={migratePayHereOpen} onOpenChange={setMigratePayHereOpen} />
      <CloudinaryMediaLibraryDialog
        open={logoLibraryOpen}
        onOpenChange={setLogoLibraryOpen}
        title="Choose company logo from Cloudinary"
        onSelect={async (asset) => {
          setUploadingLogo(true);
          try {
            await saveCompanyLogoAsset(asset, 'Company logo selected from Cloudinary');
          } catch (error) {
            toast.error(error.response?.data?.detail || error.message || 'Failed to update company logo');
          } finally {
            setUploadingLogo(false);
          }
        }}
      />
    </div>
  );
};

export default Settings;
